import React, {
  useState,
  useEffect,
  useContext,
} from "react";

import {
  auth,
  db,
} from "../firebase/firebaseConfig";

import {
  collection,
  getDocs,
  where,
  query,
  limit,
} from "firebase/firestore";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import Alertas from "./Alertas";
import ActualizarMarcaDb from "../firebase/ActualizarMarcaDb";
import useObtenerMarca from "../hooks/useObtenerMarca";

import { UserContext } from "../context/UserContext";

import {
  Container,
  Segment,
  Form,
  Button,
  Header,
  Icon,
  Message,
} from "semantic-ui-react";

const ActualizaMarca = () => {
  const user = auth.currentUser;
  const { users } = useContext(UserContext);

  const navigate = useNavigate();
  const { id } = useParams();

  const [marca] = useObtenerMarca(id);

  // ==========================================================
  // ESTADOS
  // ==========================================================

  const [
    nuevoCampo,
    setNuevoCampo,
  ] = useState("");

  /*
   * Guarda el nombre ORIGINAL de la marca.
   *
   * Es este valor el que debemos buscar en equipos
   * e ingresostcab para saber si ya fue propagado.
   */
  const [
    marcaOriginal,
    setMarcaOriginal,
  ] = useState("");

  const [
    alerta,
    cambiarAlerta,
  ] = useState({});

  const [
    estadoAlerta,
    cambiarEstadoAlerta,
  ] = useState(false);

  const [
    isSaving,
    setIsSaving,
  ] = useState(false);

  const [
    verificandoUso,
    setVerificandoUso,
  ] = useState(false);

  const [
    marcaBloqueada,
    setMarcaBloqueada,
  ] = useState(false);

  // ==========================================================
  // VOLVER
  // ==========================================================

  const volver = () => {
    navigate(
      "/misequipos/agregarmarca"
    );
  };

  // ==========================================================
  // VERIFICAR SI LA MARCA YA ESTÁ SIENDO UTILIZADA
  // ==========================================================

  const verificarMarcaEnUso =
    async (nombreMarca) => {
      if (!users?.emp_id) {
        throw new Error(
          "No se encontró la empresa del usuario"
        );
      }

      const marcaNormalizada =
        String(
          nombreMarca || ""
        )
          .toLocaleUpperCase()
          .trim();

      if (!marcaNormalizada) {
        return false;
      }

      // ======================================================
      // 1. EQUIPOS
      // ======================================================

      const qEquipos =
        query(
          collection(
            db,
            "equipos"
          ),

          where(
            "emp_id",
            "==",
            users.emp_id
          ),

          where(
            "marca",
            "==",
            marcaNormalizada
          ),

          limit(1)
        );

      const snapEquipos =
        await getDocs(
          qEquipos
        );

      if (!snapEquipos.empty) {
        return true;
      }

      // ======================================================
      // 2. INGRESOS SERVICIO TÉCNICO
      // ======================================================

      const qIngresos =
        query(
          collection(
            db,
            "ingresostcab"
          ),

          where(
            "emp_id",
            "==",
            users.emp_id
          ),

          where(
            "marca",
            "==",
            marcaNormalizada
          ),

          limit(1)
        );

      const snapIngresos =
        await getDocs(
          qIngresos
        );

      if (!snapIngresos.empty) {
        return true;
      }

      return false;
    };

  // ==========================================================
  // VERIFICAR DUPLICADO
  // ==========================================================

  const verificarNombreDuplicado =
    async (nombreMarca) => {
      if (!users?.emp_id) {
        return false;
      }

      const marcaNormalizada =
        String(
          nombreMarca || ""
        )
          .toLocaleUpperCase()
          .trim();

      if (!marcaNormalizada) {
        return false;
      }

      const qMarcas =
        query(
          collection(
            db,
            "marcas"
          ),

          where(
            "emp_id",
            "==",
            users.emp_id
          ),

          where(
            "marca",
            "==",
            marcaNormalizada
          )
        );

      const snapMarcas =
        await getDocs(
          qMarcas
        );

      /*
       * El documento actual no cuenta como duplicado.
       */
      return snapMarcas.docs.some(
        (doc) =>
          doc.id !== id
      );
    };

  // ==========================================================
  // ACTUALIZAR
  // ==========================================================

  const handleSubmit =
    async (e) => {
      e.preventDefault();

      cambiarEstadoAlerta(false);
      cambiarAlerta({});

      const nombreNuevo =
        String(
          nuevoCampo || ""
        )
          .toLocaleUpperCase()
          .trim();

      const originalNormalizado =
        String(
          marcaOriginal || ""
        )
          .toLocaleUpperCase()
          .trim();

      // ======================================================
      // VACÍO
      // ======================================================

      if (!nombreNuevo) {
        cambiarEstadoAlerta(true);

        cambiarAlerta({
          tipo: "error",
          mensaje:
            "La Marca no puede estar vacía.",
        });

        return;
      }

      // ======================================================
      // SIN CAMBIOS
      // ======================================================

      if (
        nombreNuevo ===
        originalNormalizado
      ) {
        cambiarEstadoAlerta(true);

        cambiarAlerta({
          tipo: "error",
          mensaje:
            "No se han realizado cambios en la Marca.",
        });

        return;
      }

      setIsSaving(true);

      try {
        // ====================================================
        // REVALIDAR PROPAGACIÓN
        //
        // Siempre usando la marca ORIGINAL.
        // ====================================================

        const enUso =
          await verificarMarcaEnUso(
            marcaOriginal
          );

        if (enUso) {
          setMarcaBloqueada(true);

          cambiarEstadoAlerta(true);

          cambiarAlerta({
            tipo: "error",
            mensaje:
              "Esta marca ya está siendo utilizada en el sistema y no puede modificarse.",
          });

          return;
        }

        // ====================================================
        // VALIDAR DUPLICADO
        // ====================================================

        const duplicado =
          await verificarNombreDuplicado(
            nombreNuevo
          );

        if (duplicado) {
          cambiarEstadoAlerta(true);

          cambiarAlerta({
            tipo: "error",
            mensaje:
              "Ya existe esta Marca.",
          });

          return;
        }

        // ====================================================
        // ACTUALIZAR
        // ====================================================

        await ActualizarMarcaDb({
          id:
            id,

          marca:
            nombreNuevo,

          userMod:
            user?.email || "",

          fechaMod:
            new Date(),
        });

        /*
         * El nuevo valor pasa a ser el original
         * después de actualizar correctamente.
         */
        setMarcaOriginal(
          nombreNuevo
        );

        setNuevoCampo(
          nombreNuevo
        );

        cambiarEstadoAlerta(true);

        cambiarAlerta({
          tipo: "exito",
          mensaje:
            "Marca actualizada correctamente.",
        });
      } catch (error) {
        console.error(
          "Error actualizando Marca:",
          error
        );

        cambiarEstadoAlerta(true);

        cambiarAlerta({
          tipo: "error",
          mensaje:
            "No fue posible actualizar la Marca.",
        });
      } finally {
        setIsSaving(false);
      }
    };

  // ==========================================================
  // CARGAR MARCA Y VERIFICAR USO
  // ==========================================================

  useEffect(() => {
    if (
      !marca?.marca ||
      !users?.emp_id
    ) {
      return;
    }

    const nombreMarca =
      String(
        marca.marca || ""
      )
        .toLocaleUpperCase()
        .trim();

    setNuevoCampo(
      nombreMarca
    );

    setMarcaOriginal(
      nombreMarca
    );

    const comprobarUso =
      async () => {
        setVerificandoUso(true);

        try {
          const enUso =
            await verificarMarcaEnUso(
              nombreMarca
            );

          setMarcaBloqueada(
            enUso
          );
        } catch (error) {
          console.error(
            "Error verificando uso de Marca:",
            error
          );

          /*
           * Si no podemos verificar,
           * bloqueamos por seguridad.
           */
          setMarcaBloqueada(true);

          cambiarEstadoAlerta(true);

          cambiarAlerta({
            tipo: "error",
            mensaje:
              "No fue posible verificar si la Marca puede modificarse.",
          });
        } finally {
          setVerificandoUso(false);
        }
      };

    comprobarUso();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    marca,
    users?.emp_id,
  ]);

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <Container
      fluid
      style={{
        maxWidth: 1400,
        padding: "24px 28px",
        background: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      {/* ==================================================== */}
      {/* ENCABEZADO */}
      {/* ==================================================== */}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          marginBottom: 20,
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            minWidth: 48,
            borderRadius: 12,
            background: "#e5f7f7",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon
            name="edit outline"
            style={{
              margin: 0,
              color: "#0d9da5",
              fontSize: 21,
            }}
          />
        </div>

        <div>
          <Header
            as="h2"
            style={{
              margin: 0,
              color: "#172b4d",
              fontSize: 24,
              fontWeight: 700,
            }}
          >
            Actualizar Marca
          </Header>

          <div
            style={{
              color: "#8793a5",
              fontSize: 13,
              marginTop: 4,
            }}
          >
            Modifica la marca de equipamiento médico
          </div>
        </div>
      </div>

      {/* ==================================================== */}
      {/* TARJETA */}
      {/* ==================================================== */}

      <Segment
        style={{
          borderRadius: 8,
          border: "1px solid #e5e9ef",
          boxShadow:
            "0 2px 6px rgba(0,0,0,0.04)",
          padding: 0,
          background: "#ffffff",
          overflow: "hidden",
        }}
      >
        {/* CABECERA TARJETA */}

        <div
          style={{
            padding: "14px 18px",
            borderBottom:
              "1px solid #edf0f4",
            display: "flex",
            alignItems: "center",
            gap: 9,
            color: "#253858",
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          <Icon
            name="tag"
            style={{
              color: "#0d9da5",
            }}
          />

          Datos de la Marca
        </div>

        <div
          style={{
            padding: 22,
          }}
        >
          {/* ================================================ */}
          {/* VERIFICANDO */}
          {/* ================================================ */}

          {verificandoUso && (
            <Message
              icon
              size="small"
              style={{
                marginTop: 0,
              }}
            >
              <Icon
                name="circle notched"
                loading
              />

              <Message.Content>
                <Message.Header>
                  Verificando Marca
                </Message.Header>

                Estamos comprobando si la Marca puede ser modificada.
              </Message.Content>
            </Message>
          )}

          {/* ================================================ */}
          {/* BLOQUEADA */}
          {/* ================================================ */}

          {!verificandoUso &&
            marcaBloqueada && (
              <Message
                warning
                icon
                style={{
                  marginTop: 0,
                }}
              >
                <Icon name="lock" />

                <Message.Content>
                  <Message.Header>
                    Marca protegida
                  </Message.Header>

                  Esta marca ya está siendo utilizada en el sistema y no puede modificarse.
                </Message.Content>
              </Message>
            )}

          {/* ================================================ */}
          {/* DISPONIBLE */}
          {/* ================================================ */}

          {!verificandoUso &&
            !marcaBloqueada &&
            marcaOriginal && (
              <Message
                positive
                icon
                size="small"
                style={{
                  marginTop: 0,
                }}
              >
                <Icon name="unlock" />

                <Message.Content>
                  <Message.Header>
                    Marca disponible para edición
                  </Message.Header>

                  Esta marca todavía no ha sido utilizada en el sistema.
                </Message.Content>
              </Message>
            )}

          {/* ================================================ */}
          {/* FORMULARIO */}
          {/* ================================================ */}

          <Form
            size="large"
            onSubmit={
              handleSubmit
            }
          >
            <Form.Field>
              <label
                style={{
                  color: "#344563",
                  fontSize: 13,
                  fontWeight: 600,
                  marginBottom: 7,
                  display: "block",
                }}
              >
                Marca
              </label>

              <Form.Input
                fluid
                type="text"
                name="marca"
                icon="tag"
                iconPosition="left"
                placeholder="Ingrese Marca Equipamiento Médico"
                value={
                  nuevoCampo
                }
                disabled={
                  isSaving ||
                  verificandoUso ||
                  marcaBloqueada
                }
                onChange={(e) =>
                  setNuevoCampo(
                    e.target.value
                  )
                }
              />
            </Form.Field>

            {/* ============================================== */}
            {/* BOTONES */}
            {/* ============================================== */}

            <div
              style={{
                display: "flex",
                justifyContent:
                  "flex-end",
                alignItems: "center",
                gap: 10,
                marginTop: 30,
                paddingTop: 18,
                borderTop:
                  "1px solid #edf0f4",
                flexWrap: "wrap",
              }}
            >
              <Button
                type="button"
                onClick={
                  volver
                }
                disabled={
                  isSaving
                }
                style={{
                  minWidth: 110,
                  background: "#ffffff",
                  color: "#526273",
                  border:
                    "1px solid #d7dee7",
                  borderRadius: 6,
                  boxShadow: "none",
                }}
              >
                <Icon
                  name="arrow left"
                />

                Volver
              </Button>

              {!marcaBloqueada && (
                <Button
                  type="submit"
                  loading={
                    isSaving
                  }
                  disabled={
                    isSaving ||
                    verificandoUso ||
                    !marcaOriginal
                  }
                  style={{
                    minWidth: 125,
                    background:
                      "#0d9da5",
                    color: "#ffffff",
                    border:
                      "1px solid #0d9da5",
                    borderRadius: 6,
                    boxShadow: "none",
                  }}
                >
                  {!isSaving && (
                    <Icon
                      name="save"
                    />
                  )}

                  Actualizar
                </Button>
              )}
            </div>
          </Form>
        </div>
      </Segment>

      {/* ==================================================== */}
      {/* ALERTAS */}
      {/* ==================================================== */}

      <Alertas
        tipo={
          alerta.tipo
        }
        mensaje={
          alerta.mensaje
        }
        estadoAlerta={
          estadoAlerta
        }
        cambiarEstadoAlerta={
          cambiarEstadoAlerta
        }
      />
    </Container>
  );
};

export default ActualizaMarca;